import React, { useMemo, useState } from 'react';
import { Box, Button, Checkbox, Chip, Divider, FormControlLabel, LinearProgress, Typography } from '@material-ui/core';
import { Label, MailOutline, NotificationsNone, Tune } from '@material-ui/icons';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { GET_ENTERPRISE_SUB_ACCOUNTS } from './enterprise-sub-accounts.gql';
import { GET_PUSHABLE_CONFIG_ITEMS } from './enterprise-pushable-config-items.gql';
import { PUSHABLE_CONFIG_KEYS } from './enterprise-pushable-config-keys';
import { GetEnterpriseSubAccounts } from './__generated__/GetEnterpriseSubAccounts';
import { GetPushableConfigItems } from './__generated__/GetPushableConfigItems';

interface EnterpriseSettingsPushProps {
  className?: string;
  headingClassName?: string;
  bodyClassName?: string;
}

export const EnterpriseSettingsPush: React.FC<EnterpriseSettingsPushProps> = ({
  className,
  headingClassName,
  bodyClassName,
}) => {
  const apiUrl = useApiUrl();
  const { setSnackbar } = useSnackbar();
  const pushSettings = useCampfireFetch({ defer: true });

  const { data: subAccountData } = useCampfireQuery<GetEnterpriseSubAccounts, {}>(GET_ENTERPRISE_SUB_ACCOUNTS);
  const { data: configItemData, loading: configLoading } = useCampfireQuery<GetPushableConfigItems, { keys: string[] }>(
    GET_PUSHABLE_CONFIG_ITEMS,
    { options: { variables: { keys: PUSHABLE_CONFIG_KEYS } } }
  );

  const subAccounts = subAccountData?.tm.enterpriseSubAccounts ?? [];
  const configItems = useMemo(
    () => [...(configItemData?.vm.configItems ?? [])].sort((a, b) => a.description.localeCompare(b.description)),
    [configItemData]
  );

  const [includeMenuLabels, setIncludeMenuLabels] = useState(true);
  const [includeEmailStatuses, setIncludeEmailStatuses] = useState(false);
  const [includeNotificationSettings, setIncludeNotificationSettings] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [selectedSubs, setSelectedSubs] = useState<string[]>([]);

  const toggleKey = (key: string) =>
    setSelectedKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const toggleSub = (tenantId: string) =>
    setSelectedSubs((prev) => (prev.includes(tenantId) ? prev.filter((t) => t !== tenantId) : [...prev, tenantId]));

  const targetCount = selectedSubs.length === 0 ? subAccounts.length : selectedSubs.length;
  const canPush =
    (includeMenuLabels || includeEmailStatuses || includeNotificationSettings || selectedKeys.length > 0) &&
    targetCount > 0;

  const handlePush = async () => {
    try {
      const response = await pushSettings.run({
        url: `${apiUrl}/vm/enterprise/push-settings`,
        method: 'post',
        data: {
          subTenantIds: selectedSubs,
          includeMenuLabels,
          configItemKeys: selectedKeys,
          includeEmailStatuses,
          includeNotificationSettings,
        },
      });

      if (!response.ok) {
        throw new Error('Request failed');
      }

      setSnackbar({
        variant: 'success',
        open: true,
        message: `Settings applied to ${targetCount} sub-account${targetCount === 1 ? '' : 's'}`,
      });
    } catch (error) {
      setSnackbar({ variant: 'error', open: true, message: "Couldn't push settings" });
    }
  };

  return (
    <Box className={className}>
      <Typography className={headingClassName}>
        <Tune style={{ fontSize: '14px', paddingRight: '5px' }} /> Organisation settings
      </Typography>
      <Typography variant='body2' className={bodyClassName}>
        Custom wording, feature toggles, email on/off settings and notification settings are applied to sub-accounts
        straight away rather than being offered for review.
      </Typography>

      <Box marginTop='1rem'>
        <FormControlLabel
          control={
            <Checkbox
              color='primary'
              checked={includeMenuLabels}
              onChange={(e) => setIncludeMenuLabels(e.target.checked)}
            />
          }
          label={
            <Box display='flex' alignItems='center'>
              <Label style={{ fontSize: '16px', paddingRight: '6px', color: '#666' }} />
              <Typography style={{ fontSize: '14px' }}>Custom labels</Typography>
            </Box>
          }
        />
      </Box>
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              color='primary'
              checked={includeEmailStatuses}
              onChange={(e) => setIncludeEmailStatuses(e.target.checked)}
            />
          }
          label={
            <Box display='flex' alignItems='center'>
              <MailOutline style={{ fontSize: '16px', paddingRight: '6px', color: '#666' }} />
              <Typography style={{ fontSize: '14px' }}>Which emails are switched on</Typography>
            </Box>
          }
        />
      </Box>
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              color='primary'
              checked={includeNotificationSettings}
              onChange={(e) => setIncludeNotificationSettings(e.target.checked)}
            />
          }
          label={
            <Box display='flex' alignItems='center'>
              <NotificationsNone style={{ fontSize: '16px', paddingRight: '6px', color: '#666' }} />
              <Typography style={{ fontSize: '14px' }}>
                Notification settings{' '}
                <span style={{ color: '#999' }}>(sub-accounts keep their own specific managers and custom emails)</span>
              </Typography>
            </Box>
          }
        />
      </Box>

      <Divider style={{ margin: '12px 0' }} />

      <Typography style={{ fontSize: '13px', fontWeight: 600, color: '#444', marginBottom: '6px' }}>
        Feature toggles
      </Typography>
      {configLoading ? (
        <LinearProgress />
      ) : (
        <Box display='flex' flexWrap='wrap' style={{ gap: '4px 16px' }}>
          {configItems.map((item) => (
            <FormControlLabel
              key={item.key}
              style={{ width: '46%', minWidth: '260px' }}
              control={
                <Checkbox
                  color='primary'
                  size='small'
                  checked={selectedKeys.includes(item.key)}
                  onChange={() => toggleKey(item.key)}
                />
              }
              label={
                <Typography style={{ fontSize: '13px' }}>
                  {item.description}{' '}
                  <span style={{ color: '#999' }}>
                    ({item.configItemValue?.flagValue ?? item.defaultValue ? 'on' : 'off'})
                  </span>
                </Typography>
              }
            />
          ))}
        </Box>
      )}

      <Divider style={{ margin: '12px 0' }} />

      <Typography style={{ fontSize: '13px', fontWeight: 600, color: '#444', marginBottom: '6px' }}>
        Apply to
      </Typography>
      <Box display='flex' flexWrap='wrap' style={{ gap: '6px' }}>
        <Chip
          size='small'
          label={`All sub-accounts (${subAccounts.length})`}
          color={selectedSubs.length === 0 ? 'primary' : 'default'}
          variant={selectedSubs.length === 0 ? 'default' : 'outlined'}
          onClick={() => setSelectedSubs([])}
        />
        {subAccounts.map((sub) => (
          <Chip
            key={sub.tenantId}
            size='small'
            label={sub.name}
            color={selectedSubs.includes(sub.tenantId) ? 'primary' : 'default'}
            variant={selectedSubs.includes(sub.tenantId) ? 'default' : 'outlined'}
            onClick={() => toggleSub(sub.tenantId)}
          />
        ))}
      </Box>

      <Box marginTop='1.25rem'>
        <Button
          variant='contained'
          color='primary'
          disableElevation
          disabled={!canPush || pushSettings.isLoading}
          onClick={handlePush}
        >
          {`Apply to ${targetCount} sub-account${targetCount === 1 ? '' : 's'}`}
        </Button>
      </Box>
    </Box>
  );
};
